/**
 * Recover a route whose code failed to download. Row 75a2edb9.
 *
 * Real-stack guest proofs 2026-10-01: the meeting pages are code-split, and a
 * guest whose connection changed while that code downloaded (Wi-Fi to
 * cellular, a VPN reconnect) landed on "Something went wrong". React.lazy
 * caches the REJECTED import, so the error page's "Try Again" re-rendered the
 * same failure; only a manual reload got them in.
 *
 * Usage keeps the plain `lazy(() => import("..."))` shape that the route
 * scanners read, and adds the recovery as the rejection handler:
 *
 *   lazy(() => import("@/pages/MeetingRoom").catch(
 *     recoverChunk(() => import("@/pages/MeetingRoom"), "MeetingRoom")))
 *
 * On a download failure it retries the import (with a short backoff), and if
 * that still fails it reloads the page ONCE: a per-route sessionStorage stamp
 * refuses a second reload for RELOAD_GUARD_MS, so a page that cannot load
 * shows its error instead of reloading forever. Any other error (a bug in the
 * module itself) is rethrown untouched.
 *
 * Researched: Vite's "Load Error Handling" guidance (vite:preloadError, reload
 * on a failed chunk) and React.lazy's caching of a rejected promise. A failed
 * CSS preload is not retried: Vite's preload helper remembers it has "seen"
 * that stylesheet, so a retry would load the page without its styles -- a
 * reload is the only clean recovery there.
 */

/** Matches the download failures of the browsers and of Vite's preload helper. */
const CHUNK_ERROR = new RegExp(
  [
    "Failed to fetch dynamically imported module", // Chromium
    "error loading dynamically imported module", // Firefox
    "Importing a module script failed", // Safari
    "Unable to preload CSS", // Vite preload helper
    "Loading (CSS )?chunk [^ ]+ failed",
  ].join("|"),
  "i",
);

export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const e = err as { name?: unknown; message?: unknown };
  if (e.name === "ChunkLoadError") return true;
  const msg = typeof e.message === "string" ? e.message : String(err);
  return CHUNK_ERROR.test(msg);
}

function isCssPreloadError(err: unknown): boolean {
  const msg = (err as { message?: unknown })?.message;
  return typeof msg === "string" && msg.includes("Unable to preload CSS");
}

export const CHUNK_RELOAD_KEY_PREFIX = "ftl_chunk_reload:";
/** The ErrorBoundary's own auto-reload stamp; set too, so it does not reload again. */
const BOUNDARY_RELOAD_KEY = "_vite_reload_ts";
export const RELOAD_GUARD_MS = 10 * 60 * 1000;

export interface RecoverOptions {
  retries?: number;
  delayMs?: number;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  reload?: () => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

function defaultStorage(): RecoverOptions["storage"] | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

export function recoverChunk<T>(
  factory: () => Promise<T>,
  name: string,
  opts: RecoverOptions = {},
): (err: unknown) => Promise<T> {
  const {
    retries = 2,
    delayMs = 600,
    reload = () => window.location.reload(),
    sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms)),
    now = () => Date.now(),
  } = opts;
  const key = CHUNK_RELOAD_KEY_PREFIX + name;

  return async (firstError: unknown): Promise<T> => {
    if (!isChunkLoadError(firstError)) throw firstError;
    const storage = opts.storage ?? defaultStorage();
    let lastError: unknown = firstError;

    if (!isCssPreloadError(firstError)) {
      for (let attempt = 1; attempt <= retries; attempt += 1) {
        await sleep(delayMs * attempt);
        try {
          const mod = await factory();
          try { storage?.removeItem(key); } catch { /* storage unavailable */ }
          return mod;
        } catch (err) {
          if (!isChunkLoadError(err)) throw err;
          lastError = err;
        }
      }
    }

    // Still failing: reload once, unless this route already did so recently.
    let last = 0;
    try { last = Number(storage?.getItem(key)) || 0; } catch { last = 0; }
    if (storage && now() - last > RELOAD_GUARD_MS) {
      try {
        storage.setItem(key, String(now()));
        storage.setItem(BOUNDARY_RELOAD_KEY, String(now()));
      } catch {
        // Without storage the guard cannot hold, so do not reload at all.
        throw lastError;
      }
      console.warn(`[chunk] ${name} failed to download; reloading once`, lastError);
      reload();
      // The page is going away; never settle, so nothing renders the error.
      return new Promise<T>(() => {});
    }
    throw lastError;
  };
}
